import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

interface ContinueArchivesStatus {
  isGuest: boolean;
  dailyCompletedToday: boolean;
  archivesPlayedCount: number;
  archivesTotal: number;
  nextArchivePuzzleNumber: number | null;
  shouldPromptConvert: boolean;
}

interface ContinueArchivesBlockProps {
  isArchive?: boolean;
}

/** CTA « jouer une archive » pour comptes connectés (non-invités). */
export function ContinueArchivesBlock({ isArchive = false }: ContinueArchivesBlockProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ContinueArchivesStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.isGuest) {
      setLoading(false);
      return;
    }
    api<ContinueArchivesStatus>('/onboarding/continue-archives')
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user || user.isGuest || loading || !status || status.isGuest) return null;
  if (!status.nextArchivePuzzleNumber) return null;

  const { archivesPlayedCount, archivesTotal, nextArchivePuzzleNumber, dailyCompletedToday } = status;
  const progressPct = archivesTotal > 0
    ? Math.round((archivesPlayedCount / archivesTotal) * 100)
    : 0;

  const goNext = () => navigate(`/archives/${nextArchivePuzzleNumber}/play`);

  if (!isArchive && dailyCompletedToday) {
    return (
      <div className="guest-onboarding continue-archives">
        <h2 className="guest-onboarding-title">{t('onboarding.continueArchivesTitle')}</h2>
        <p className="muted">{t('onboarding.continueArchivesHint')}</p>
        <button type="button" className="btn primary block" onClick={goNext}>
          {t('onboarding.guestArchivesCta')}
        </button>
      </div>
    );
  }

  if (isArchive) {
    return (
      <div className="guest-onboarding continue-archives">
        {archivesPlayedCount > 0 && archivesTotal > 0 && (
          <>
            <p className="guest-onboarding-progress-label">
              {t('onboarding.continueProgress')
                .replace('{done}', String(archivesPlayedCount))
                .replace('{total}', String(archivesTotal))}
            </p>
            <div className="guest-onboarding-progress" aria-hidden="true">
              <div className="guest-onboarding-progress-bar" style={{ width: `${progressPct}%` }} />
            </div>
          </>
        )}
        <button type="button" className="btn primary block" onClick={goNext}>
          {t('onboarding.nextArchive')} →
        </button>
      </div>
    );
  }

  return null;
}
